/**
 * The six views of "My inventions" (Story #5648 build v2, figures S4 and S4b).
 *
 * The rail — the zoom notches in the top bar — never moves on this page. What
 * changes is the VIEW: the same set of inventions, read a different way. The
 * figure shows all six as one tab strip, so all six are built here and each one
 * leads to a real screen driven by a real own-scope route.
 *
 * `list` is the bare /projects URL, so an existing link to /projects keeps
 * working and lands on the tab a reader expects.
 */
export type OwnScopeView = 'list' | 'atlas' | 'synergy' | 'overlap' | 'shape' | 'watch'

export interface OwnScopeTab {
  view: OwnScopeView
  label: string
  /** Where the tab goes. `list` carries no ?view so /projects stays canonical. */
  to: string
  /** A REAL key in constants/helpContent.ts — the "?" beside the page title. */
  helpKey: string
}

export const OWN_SCOPE_TABS: OwnScopeTab[] = [
  { view: 'list', label: 'List', to: '/projects', helpKey: 'projects' },
  { view: 'atlas', label: 'Atlas', to: '/projects?view=atlas', helpKey: 'pf-atlas' },
  { view: 'synergy', label: 'Synergy', to: '/projects?view=synergy', helpKey: 'pf-synergy' },
  { view: 'overlap', label: 'Overlap', to: '/projects?view=overlap', helpKey: 'pf-overlap' },
  { view: 'shape', label: 'Shape & Strategy', to: '/projects?view=shape', helpKey: 'pf-shape' },
  { view: 'watch', label: 'Watch', to: '/projects?view=watch', helpKey: 'pf-watch' },
]

/** `?view=` → a built view. Anything unrecognised (and a missing value) is the
 * List tab, which is what the bare /projects URL has always shown. */
export function resolveOwnScopeView(raw: string | null | undefined): OwnScopeView {
  const match = OWN_SCOPE_TABS.find((t) => t.view === raw)
  return match ? match.view : 'list'
}

export function ownScopeTabFor(view: OwnScopeView): OwnScopeTab {
  return OWN_SCOPE_TABS.find((t) => t.view === view) ?? OWN_SCOPE_TABS[0]
}

/** The figure's one-line description under the page title — the same sentence
 * on every view, because the page is one place read six ways. */
export const OWN_SCOPE_DESCRIPTION =
  'Every invention you own — as a list, or on one atlas with overlap, synergy and white space between them.'

/** The entitlement clause the figures print under every computed number. */
export const OWN_SCOPE_ENTITLEMENT_LINE =
  "computed over everything you're entitled to see · scope: your inventions"
